import React from "react";

/**
 * SidebarFormLayout — wraps a react-admin <TabbedForm> and transforms the
 * horizontal tab bar into a vertical sidebar navigation via CSS.
 *
 * Usage (wrap around Edit/Create, NOT inside — react-admin v3 requires the
 * form to be the direct child of Edit/Create for save prop injection):
 *
 *   <SidebarFormLayout>
 *     <Edit {...props}>
 *       <TabbedForm tabs={<TabbedFormTabs orientation="vertical" />}>
 *         <FormTab label="…">…</FormTab>
 *       </TabbedForm>
 *     </Edit>
 *   </SidebarFormLayout>
 *
 * react-admin v3's TabbedFormView always renders 4 direct children inside the
 * <form>:  Tabs · Divider · ContentPanel · Toolbar.
 * The CSS below re-arranges them into a sidebar + content layout using flexbox.
 */

const css = `
/* ── Layout ──────────────────────────────────────────── */
.sidebar-tabbed-form form {
  display: flex;
  flex-wrap: wrap;
  max-width: 100%;
}

/* Sidebar navigation (the MUI Tabs container) */
.sidebar-tabbed-form form > :first-child {
  flex: 0 0 210px;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  background: #fafafa;
  align-self: stretch;
  min-height: 420px;
  padding-top: 8px;
}

/* Hide the horizontal divider that normally sits between tabs & content */
.sidebar-tabbed-form form > hr {
  display: none;
}

/* Content area — everything after Tabs + Divider, before Toolbar */
.sidebar-tabbed-form form > :nth-child(3) {
  flex: 1;
  min-width: 0;
  padding: 16px 24px;
  max-width: calc(100% - 210px);
  overflow-x: hidden;
}

/* Toolbar (Save button) spans full width below.
   Target by class — :last-child breaks when react-admin appends extra elements. */
.sidebar-tabbed-form form > [class*="RaToolbar"] {
  flex: 0 0 100%;
}

/* ── Tab buttons ─────────────────────────────────────── */
.sidebar-tabbed-form .MuiTab-root {
  text-transform: none;
  min-height: 36px;
  font-size: 13px;
  padding: 6px 16px;
  white-space: normal;
  line-height: 1.3;
}
.sidebar-tabbed-form .MuiTab-wrapper {
  align-items: flex-start;
}

/* Thinner vertical indicator on the right edge of the sidebar */
.sidebar-tabbed-form .MuiTabs-indicator {
  width: 3px;
}

/* ── Reduce TopToolbar empty space (the action bar above the form) ── */
div[class*="RaTopToolbar"] {
  min-height: auto !important;
  padding: 4px 8px !important;
  align-items: center;
}
`;

const SidebarFormLayout = ({ children }) => (
  <div className="sidebar-tabbed-form">
    <style>{css}</style>
    {children}
  </div>
);

export default SidebarFormLayout;
